import React, { useState, useEffect } from "react";
import companyService from "@/services/companyService";
import { copyTasksToCustomerAssurance } from "@/services/frameworkService";
import { useTenancy } from "@/contexts/TenancyContext";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";

interface ISO42001State {
  enabled: "enabled" | "disabled";
}

const defaultISO42001: ISO42001State = {
  enabled: "disabled",
};

export default function ISO42001Module() {
  const { activeTenancy } = useTenancy();
  const [iso42001, setIso42001] = useState<ISO42001State>(defaultISO42001);
  const [iso42001Initial, setIso42001Initial] = useState<ISO42001State | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchISO42001 = async () => {
      setIsLoading(true);
      try {
        const settings = await companyService.getCompanySettings(undefined, "complianceModules");
        const iso42001Settings = settings?.["iso42001"] ?? {};
        // Precedence: 'Enabled' (case-insensitive), then 'active', 'enabled', 'Active', etc.
        let isEnabled = false;
        // Helper to check both string and boolean
        function checkEnabled(val: unknown): boolean {
          if (typeof val === 'boolean') return val;
          if (typeof val === 'string') return val.toLowerCase() === 'true' || val.toLowerCase() === 'enabled';
          return false;
        }
        if (typeof iso42001Settings.Enabled !== 'undefined') {
          isEnabled = checkEnabled(iso42001Settings.Enabled);
        } else if (typeof iso42001Settings.enabled !== 'undefined') {
          isEnabled = checkEnabled(iso42001Settings.enabled);
        } else if (typeof iso42001Settings.active !== 'undefined') {
          isEnabled = checkEnabled(iso42001Settings.active);
        } else if (typeof iso42001Settings.Active !== 'undefined') {
          isEnabled = checkEnabled(iso42001Settings.Active);
        }
        setIso42001({ enabled: isEnabled ? "enabled" : "disabled" });
        setIso42001Initial({ enabled: isEnabled ? "enabled" : "disabled" });
      } catch (err) {
        toast.error("Failed to fetch ISO 42001 settings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchISO42001();
  }, []);

  const iso42001Changed = iso42001Initial && JSON.stringify(iso42001) !== JSON.stringify(iso42001Initial);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const wasDisabled = iso42001Initial?.enabled === "disabled";
      const isNowEnabled = iso42001.enabled === "enabled";
      
      await companyService.saveCompanySettings(undefined, "complianceModules", {
        iso42001: { Enabled: iso42001.enabled === "enabled" },
      });
      
      // DED-1511: If ISO 42001 is being enabled (was disabled, now enabled), copy tasks
      if (wasDisabled && isNowEnabled && activeTenancy?.id) {
        try {
          const customerId = activeTenancy.id;
          
          toast.info("Copying ISO 42001 tasks...");
          
          await copyTasksToCustomerAssurance(
            customerId,
            'ISO42001',
            undefined,
            undefined
          );
          
          toast.success("ISO 42001 settings saved and tasks copied");
        } catch (taskError) {
          console.error('Error copying ISO 42001 tasks:', taskError);
          toast.warning("Settings saved, but there was an error copying some tasks. Please check the console.");
        }
      } else {
        toast.success("ISO 42001 settings saved");
      }
      
      setIso42001Initial({ ...iso42001 });
    } catch (err) {
      toast.error("Failed to save ISO 42001 settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-muted/30 p-6 rounded-lg border border-border space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-green-500/10 p-3 rounded-full">
          <span role="img" aria-label="ISO 42001">🌐</span>
        </div>
        <div>
          <div className="font-semibold text-lg">ISO 42001</div>
          <div className="text-xs text-muted-foreground">Information Security Management System Module</div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Module Status</label>
        <Select
          value={iso42001.enabled}
          onValueChange={val => setIso42001(v => ({ ...v, enabled: val as ISO42001State["enabled"] }))}
          disabled={isLoading || isSaving}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2 mt-4">
        <Button onClick={handleSave} disabled={!iso42001Changed || isSaving || isLoading}>
          {isSaving ? "Saving..." : "Save ISO 42001 Settings"}
        </Button>
      </div>
    </div>
  );
}
